import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { RoleLevel } from '../../types/rbacImproved';
import styled, { keyframes } from 'styled-components';
// Animations
const fadeUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const fadeIn = keyframes `
  from { opacity: 0; }
  to { opacity: 1; }
`;
const shimmer = keyframes `
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;
// Page Container
const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 24px 80px;

  @media (max-width: 768px) {
    padding: 40px 16px 60px;
  }
`;
const LoginContainer = styled.div `
  width: 100%;
  max-width: 480px;
  animation: ${fadeUp} 0.8s ease-out;
`;
// Logo/Brand Section
const BrandSection = styled.div `
  text-align: center;
  margin-bottom: 40px;
`;
const BrandTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 400;
  color: var(--text-primary);
  margin-bottom: 8px;
  letter-spacing: 0.05em;

  span {
    color: var(--accent-gold, #B8860B);
  }
`;
const BrandSubtitle = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;
// Login Card
const LoginCard = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 40px;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg,
      transparent,
      var(--accent-gold, #B8860B) 20%,
      var(--accent-gold, #B8860B) 80%,
      transparent
    );
  }

  &:hover {
    border-color: var(--border-color-hover);
    box-shadow: var(--shadow-lg);
  }

  @media (max-width: 480px) {
    padding: 28px 20px;
  }
`;
const FormHeader = styled.div `
  text-align: center;
  margin-bottom: 32px;
`;
const FormTitle = styled.h2 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
  letter-spacing: 0.02em;
`;
const FormSubtitle = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
`;
// Form Elements
const FormGroup = styled.div `
  margin-bottom: 20px;
`;
const FormRow = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
const Label = styled.label `
  display: block;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;
const Input = styled.input `
  width: 100%;
  padding: 14px 16px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.3s ease;

  &::placeholder {
    color: var(--text-tertiary);
  }

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
    box-shadow: 0 0 0 3px var(--accent-gold-bg, rgba(184, 134, 11, 0.1));
  }

  &:hover:not(:focus) {
    border-color: var(--border-color-hover);
  }
`;
const CheckboxWrapper = styled.div `
  margin: 20px 0;
`;
const CheckboxLabel = styled.label `
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  cursor: pointer;
  line-height: 1.5;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    accent-color: var(--accent-gold, #B8860B);
    cursor: pointer;
  }
`;
const ErrorMessage = styled.div `
  color: var(--error-color, #ef4444);
  padding: 12px 16px;
  border: 1px solid var(--error-color, #ef4444);
  background: var(--error-bg, rgba(239, 68, 68, 0.1));
  margin-bottom: 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
`;
const SuccessMessage = styled.div `
  color: var(--success-color, #22c55e);
  padding: 12px 16px;
  border: 1px solid var(--success-color, #22c55e);
  background: var(--success-bg, rgba(34, 197, 94, 0.1));
  margin-bottom: 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
`;
const SubmitButton = styled.button `
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  background: var(--accent-gold, #B8860B);
  color: white;
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  margin-top: 8px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover:not(:disabled) {
    background: var(--accent-gold-dark, #8B6914);
    transform: translateY(-2px);

    &::before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const FormFooter = styled.div `
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);
  text-align: center;
`;
const FooterText = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
`;
const FooterLink = styled.a `
  color: var(--accent-gold, #B8860B);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    text-decoration: underline;
  }
`;
// Benefits Section
const BenefitsSection = styled.div `
  margin-top: 40px;
  animation: ${fadeUp} 0.8s ease-out 0.4s backwards;
`;
const BenefitsList = styled.ul `
  list-style: none;
  padding: 0;
  margin: 0;
`;
const BenefitItem = styled.li `
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid var(--border-color);
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    padding-left: 8px;
    color: var(--text-primary);
  }
`;
const CheckIcon = styled.span `
  color: var(--accent-gold, #B8860B);
  font-size: 16px;
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 2px;
`;
// Info Section
const InfoSection = styled.div `
  margin-top: 40px;
  padding: 28px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  text-align: center;
  position: relative;
  animation: ${fadeUp} 0.8s ease-out 0.6s backwards;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: var(--accent-gold, #B8860B);
  }
`;
const InfoTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;
const InfoText = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const HighlightText = styled.span `
  color: var(--accent-gold, #B8860B);
  font-weight: 500;
`;
const Login = () => {
    const navigate = useNavigate();
    const zustandAuth = useAuthStore();
    const { isLoading } = zustandAuth;
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        businessName: '',
        phone: '',
        rememberMe: false,
        acceptTerms: false,
    });
    const [isLoginMode, setIsLoginMode] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    // Automatic redirect if user is already authenticated
    useEffect(() => {
        if (zustandAuth.isAuthenticated && zustandAuth.user && !isLoading) {
            console.log('🔄 Login - User already authenticated, redirecting...', {
                userRole: zustandAuth.user.role,
                isAuthenticated: zustandAuth.isAuthenticated
            });
            // Redirect based on user role
            if (zustandAuth.user.roleLevel >= RoleLevel.PLATFORM_ADMIN) {
                // Platform/Super admin - stay put to preserve state
                console.log('🔐 Admin detected - avoiding redirect to preserve auth state');
            }
            else if (zustandAuth.user.roleLevel >= RoleLevel.SALES_MANAGER) {
                // Admin users go to user management
                navigate('/admin/users');
            }
            else if (zustandAuth.user.roleLevel >= RoleLevel.BASIC_DEALER) {
                // Dealers go to news
                navigate('/news');
            }
            else {
                // Basic users go to home
                navigate('/');
            }
        }
    }, [zustandAuth.isAuthenticated, zustandAuth.user, isLoading]);
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        try {
            if (isLoginMode) {
                // Use Zustand store for reliable login
                console.log('🔐 Login - Using Zustand auth store...');
                const result = await zustandAuth.login(formData.email, formData.password, formData.rememberMe);
                if (result.success) {
                    setSuccess('Login successful! Welcome back!');
                    console.log('🔐 Login - Success, auth state updated');
                    // Small delay to let state update, then redirect based on role
                    setTimeout(() => {
                        const user = zustandAuth.user;
                        if (user) {
                            if (user.roleLevel >= RoleLevel.PLATFORM_ADMIN) {
                                // Super admin - avoid redirect to preserve state
                                console.log('🔐 Super admin login - staying put to preserve auth state');
                            }
                            else if (user.roleLevel >= RoleLevel.SALES_MANAGER) {
                                // Admin users go to user management
                                navigate('/admin/users');
                            }
                            else if (user.roleLevel >= RoleLevel.BASIC_DEALER) {
                                // Dealers go to news
                                navigate('/news');
                            }
                            else {
                                // Basic users go to home
                                navigate('/');
                            }
                        }
                        else {
                            // Fallback to news if no user found
                            navigate('/news');
                        }
                    }, 1000);
                }
                else {
                    setError(result.error || 'Login failed');
                    console.log('🔐 Login - Failed:', result.error);
                }
            }
            else {
                // Registration
                if (!formData.acceptTerms) {
                    setError('You must accept the terms and conditions');
                    return;
                }
                const inviteToken = new URLSearchParams(window.location.search).get('invite');
                const result = await zustandAuth.register({
                    email: formData.email,
                    password: formData.password,
                    profile: {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        businessName: formData.businessName,
                        phone: formData.phone,
                    },
                    acceptTerms: formData.acceptTerms,
                    ...(inviteToken ? { inviteToken } : {}),
                });
                if (result.success) {
                    setSuccess('Account created successfully! Redirecting to your dashboard...');
                    console.log('🔐 Registration - Success, user automatically logged in');
                    // Small delay to let state update, then redirect based on role
                    setTimeout(() => {
                        const user = zustandAuth.user;
                        if (user) {
                            if (user.roleLevel >= RoleLevel.PLATFORM_ADMIN) {
                                // Super admin - avoid redirect to preserve state
                                console.log('🔐 Super admin login - staying put to preserve auth state');
                            }
                            else if (user.roleLevel >= RoleLevel.SALES_MANAGER) {
                                // Admin users go to user management
                                navigate('/admin/users');
                            }
                            else if (user.roleLevel >= RoleLevel.BASIC_DEALER) {
                                // Dealers go to news
                                navigate('/news');
                            }
                            else {
                                // Basic users go to home
                                navigate('/');
                            }
                        }
                        else {
                            // Fallback to news if no user found
                            navigate('/news');
                        }
                    }, 1500);
                }
                else {
                    setError(result.error || 'Registration failed');
                }
            }
        }
        catch (error) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Auth error:', error);
        }
    };
    return (_jsx(PageWrapper, { children: _jsxs(LoginContainer, { children: [_jsxs(BrandSection, { children: [_jsxs(BrandTitle, { children: ["Royal ", _jsx("span", { children: "Gems" })] }), _jsx(BrandSubtitle, { children: "Wholesale Diamond Trading" })] }), _jsxs(LoginCard, { children: [_jsxs(FormHeader, { children: [_jsx(FormTitle, { children: isLoginMode ? 'Welcome Back' : 'Create Account' }), _jsx(FormSubtitle, { children: isLoginMode
                                        ? 'Access your exclusive diamond inventory'
                                        : 'Join Royal Gems for exclusive access' })] }), _jsxs("form", { onSubmit: handleSubmit, children: [!isLoginMode && (_jsxs(FormRow, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "firstName", children: "First Name *" }), _jsx(Input, { type: "text", id: "firstName", name: "firstName", value: formData.firstName, onChange: handleInputChange, required: true, placeholder: "John" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "lastName", children: "Last Name *" }), _jsx(Input, { type: "text", id: "lastName", name: "lastName", value: formData.lastName, onChange: handleInputChange, required: true, placeholder: "Smith" })] })] })), _jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "email", children: "Email Address *" }), _jsx(Input, { type: "email", id: "email", name: "email", value: formData.email, onChange: handleInputChange, required: true, placeholder: "your@email.com" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "password", children: "Password *" }), _jsx(Input, { type: "password", id: "password", name: "password", value: formData.password, onChange: handleInputChange, required: true, placeholder: "Enter your password", autoComplete: "current-password" })] }), !isLoginMode && (_jsxs(_Fragment, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "businessName", children: "Business Name" }), _jsx(Input, { type: "text", id: "businessName", name: "businessName", value: formData.businessName, onChange: handleInputChange, placeholder: "Your jewelry business" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "phone", children: "Phone Number" }), _jsx(Input, { type: "tel", id: "phone", name: "phone", value: formData.phone, onChange: handleInputChange, placeholder: "Your phone number" })] })] })), _jsx(CheckboxWrapper, { children: isLoginMode ? (_jsxs(CheckboxLabel, { children: [_jsx("input", { type: "checkbox", name: "rememberMe", checked: formData.rememberMe, onChange: handleInputChange }), "Remember me for 30 days"] })) : (_jsxs(CheckboxLabel, { children: [_jsx("input", { type: "checkbox", name: "acceptTerms", checked: formData.acceptTerms, onChange: handleInputChange, required: true }), "I accept the terms and conditions and privacy policy"] })) }), error && _jsx(ErrorMessage, { children: error }), success && _jsx(SuccessMessage, { children: success }), _jsx(SubmitButton, { type: "submit", disabled: isLoading, children: isLoading
                                        ? (isLoginMode ? 'Signing In...' : 'Creating Account...')
                                        : (isLoginMode ? 'Sign In' : 'Create Account') })] }), _jsxs(FormFooter, { children: [_jsxs(FooterText, { children: [isLoginMode ? "Don't have an account? " : "Already have an account? ", _jsx(FooterLink, { href: "#", onClick: (e) => { e.preventDefault(); setIsLoginMode(!isLoginMode); setError(''); setSuccess(''); }, children: isLoginMode ? 'Create Account' : 'Sign In' })] }), isLoginMode && (_jsx(FooterText, { children: _jsx(FooterLink, { href: "/forgot-password", onClick: (e) => {
                                            e.preventDefault();
                                            navigate('/forgot-password');
                                        }, children: "Forgot your password?" }) }))] })] }), _jsx(BenefitsSection, { children: _jsxs(BenefitsList, { children: [_jsxs(BenefitItem, { children: [_jsx(CheckIcon, { children: "\u2713" }), "Access to exclusive inventory of 0.40 to 20.00+ carat diamonds"] }), _jsxs(BenefitItem, { children: [_jsx(CheckIcon, { children: "\u2713" }), "Competitive wholesale pricing with volume discounts"] }), _jsxs(BenefitItem, { children: [_jsx(CheckIcon, { children: "\u2713" }), "24/7 Priority overnight shipping available"] }), _jsxs(BenefitItem, { children: [_jsx(CheckIcon, { children: "\u2713" }), "Memo terms available (subject to credit approval)"] }), _jsxs(BenefitItem, { children: [_jsx(CheckIcon, { children: "\u2713" }), "Real-time inventory updates and notifications"] })] }) }), _jsxs(InfoSection, { children: [_jsx(InfoTitle, { children: "Royal Gems Platform Access" }), _jsxs(InfoText, { children: ["Our ", _jsx(HighlightText, { children: "Royal Gems Platform" }), " provides registered dealers with advanced search capabilities, real-time inventory tracking, and exclusive access to our complete diamond collection."] })] })] }) }));
};
export default Login;
